import { useCallback, useMemo } from "react";
import { useLazyQuery } from "@apollo/client/react";
import { type ResultOf, type VariablesOf } from "gql.tada";
import { GET_CALCULATION_SUMMARIES } from "../graphql/queries";
import type {
  CalculationEntry,
  CalculationFilters,
} from "../components/CalculationInputList/CalculationInputList.types";
import { defaultPageSize } from "../components/CalculationInputList/CalculationInputList.constants";

type CalculationSummaryItem = ResultOf<
  typeof GET_CALCULATION_SUMMARIES
>["calculationSummaries"]["content"][number];

type CalculationPage = ResultOf<
  typeof GET_CALCULATION_SUMMARIES
>["calculationSummaries"];

type CalculationFilterInput = NonNullable<
  VariablesOf<typeof GET_CALCULATION_SUMMARIES>["filter"]
>;

export function toCalculationFilterInput(
  filters: CalculationFilters,
): CalculationFilterInput | undefined {
  const filter: CalculationFilterInput = {};
  const name = filters.name.trim();
  if (name) filter.name = name;
  if (filters.lines.length) filter.lines = filters.lines;
  if (filters.supplierIds.length) filter.supplierIds = filters.supplierIds;
  if (filters.kamNames.length) filter.kamNames = filters.kamNames;
  if (filters.planningYear !== null) filter.planningYear = filters.planningYear;
  if (filters.status !== null)
    filter.status = filters.status as CalculationFilterInput["status"];
  return Object.keys(filter).length > 0 ? filter : undefined;
}

export const mapToEntry = (
  summary: CalculationSummaryItem,
): CalculationEntry => ({
  id: summary.id,
  name: summary.name,
  line: summary.line,
  supplierName: summary.supplierName,
  planningYear: summary.planningYear,
  keyAccountManager: summary.kamName,
  status: summary.status,
  lastEditedAt: summary.updatedAt,
  costCategories: summary.categoryNames,
});

export interface CalculationPageInfo {
  currentPage: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  displayPage: number;
  displayTotalPages: number;
}

export const mapToPageInfo = (
  page?: CalculationPage | null,
): CalculationPageInfo => {
  const currentPage = page?.page ?? 0;
  const totalPages = page?.totalPages ?? 0;
  const displayTotalPages = Math.max(totalPages, 1);
  const displayPage =
    totalPages === 0 ? 1 : Math.min(currentPage + 1, displayTotalPages);

  return {
    currentPage,
    pageSize: page?.size ?? defaultPageSize,
    totalElements: page?.totalElements ?? 0,
    totalPages,
    displayPage,
    displayTotalPages,
  };
};

const useCalculationEntries = () => {
  const [fetchSummaries, { data, loading, called }] = useLazyQuery(
    GET_CALCULATION_SUMMARIES,
  );

  const refetch = useCallback(
    (filter?: CalculationFilterInput, page = 0, size = defaultPageSize) => {
      fetchSummaries({ variables: { filter, page, size } });
    },
    [fetchSummaries],
  );

  const entries: CalculationEntry[] = useMemo(
    () => (data?.calculationSummaries?.content ?? []).map(mapToEntry),
    [data],
  );

  const pageInfo = useMemo(
    () => mapToPageInfo(data?.calculationSummaries),
    [data],
  );

  return { entries, pageInfo, loading, called, refetch };
};

export default useCalculationEntries;
